import { Ban, CheckCircle2, PackageX, RotateCcw, Shapes, Tags } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { ErrorState } from '../../../../components/ErrorState';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { Skeleton } from '../../../../components/Skeleton';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { EvidenceVocabularyModal } from '../components/EvidenceVocabularyModal';
import { VOCABULARIES, type VocabularyItem, type VocabularyKey } from '../evidenceVocabulary';

const KEPT_AS = (item: VocabularyItem) => (item.storesFile ? 'Stored as a file' : 'Kept on a shelf');

/** Settings → Kinds. */
export function EvidenceKindsIndexPage() {
  return <VocabularyIndexPage vocabulary="kinds" />;
}

/** Settings → Types. */
export function EvidenceTypesIndexPage() {
  return <VocabularyIndexPage vocabulary="types" />;
}

export function EvidenceDisposalTypesIndexPage() {
  return <VocabularyIndexPage vocabulary="disposal-types" />;
}

export function EvidenceDisposalTypeProfilePage() {
  return <VocabularyProfilePage vocabulary="disposal-types" />;
}

export function EvidenceKindProfilePage() {
  return <VocabularyProfilePage vocabulary="kinds" />;
}

export function EvidenceTypeProfilePage() {
  return <VocabularyProfilePage vocabulary="types" />;
}

/**
 * A kind or a type, listed. A delete is a plain soft delete — no password: retiring a word takes
 * no evidence anywhere, and every item already carrying it keeps it.
 */
function VocabularyIndexPage({ vocabulary }: { vocabulary: VocabularyKey }) {
  const config = VOCABULARIES[vocabulary];
  const navigate = useNavigate();
  const toast = useToast();

  const [editing, setEditing] = useState<VocabularyItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{ action: 'deactivate' | 'activate'; item: VocabularyItem } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchItems = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      config.api.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [config],
  );

  const rowActions = useCallback(
    (item: VocabularyItem): MenuItem[] => [
      {
        key: `restore-${item.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: `${config.module}.delete`,
        disabledReason: item.isActive ? `This ${config.singular} is not deleted.` : null,
        onClick: () => setConfirming({ action: 'activate', item }),
      },
    ],
    [config],
  );

  const columns = useMemo<ColumnDef<VocabularyItem>[]>(
    () => [
      { key: 'name', header: 'Name', width: 240, sortable: true, render: (item) => <TextCell value={item.name} /> },
      ...(config.hasFileFlag
        ? [{ key: 'storesFile', header: 'Kept as', width: 160, summary: true, render: (item: VocabularyItem) => <TextCell value={KEPT_AS(item)} /> }]
        : []),
      { key: 'description', header: 'Description', width: 360, flex: true, summary: true, render: (item) => <TextCell value={item.description} /> },
      {
        key: 'status',
        header: 'Status',
        width: 110,
        status: true,
        render: (item) => (item.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>),
      },
    ],
    [config],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.action === 'deactivate') await config.api.deactivate(confirming.item.id);
      else await config.api.activate(confirming.item.id);

      toast.success(`${confirming.item.name} was ${confirming.action === 'deactivate' ? 'deleted' : 'restored'}.`);
      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<VocabularyItem>
        module={config.module}
        breadcrumb={['Evidence Management', { label: 'Settings', to: '/evidence/settings' }, config.title]}
        columns={columns}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={[config.entityType]}
        rowKey={(item) => item.id}
        fetch={fetchItems}
        reloadKey={reloadKey}
        searchPlaceholder="Search name or description"
        emptyMessage={config.emptyMessage}
        onCreate={() => setCreating(true)}
        createPermission={`${config.module}.create`}
        onRowOpen={(item) => navigate(`${config.route}/${item.id}`)}
        openPermission={`${config.module}.read`}
        onEdit={(item) => setEditing(item)}
        editPermission={`${config.module}.update`}
        rowActions={rowActions}
        onDelete={(item) => setConfirming({ action: 'deactivate', item })}
        deletePermission={`${config.module}.delete`}
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(item) => (item.isActive ? null : `This ${config.singular} is already deleted.`)}
        otherInfo={(item) => item}
      />

      {creating || editing !== null ? (
        <EvidenceVocabularyModal
          vocabulary={vocabulary}
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            toast.success(editing ? `${editing.name} was saved.` : `The ${config.singular} was added.`);
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={`${confirming.action === 'deactivate' ? 'Delete' : 'Restore'} ${config.singular}`}
          danger={confirming.action === 'deactivate'}
          requireConfirm={confirming.action === 'deactivate'}
          loading={running}
          id={`evidence-${config.slug}-${confirming.action}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.action === 'deactivate'
                  ? `"${confirming.item.name}" stops being offered when evidence is received. Every item already carrying it keeps it.`
                  : `"${confirming.item.name}" is offered again when evidence is received. A restore is refused when a live ${config.singular} has taken its name meanwhile.`}
              </p>
              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

/** A thin profile — a vocabulary row has little to say, and an index page is always partnered with one. */
function VocabularyProfilePage({ vocabulary }: { vocabulary: VocabularyKey }) {
  const config = VOCABULARIES[vocabulary];
  const { id } = useParams<{ id: string }>();
  const prefix = `evidence-${config.slug}-profile`;

  const [item, setItem] = useState<VocabularyItem | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [confirmingRestore, setConfirmingRestore] = useState(false);
  const [tab, setTab] = useState<'info'>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      config.api
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;
          setItem(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError(`Could not load this ${config.singular}.`, 0));
        });
    },
    [id, config],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  async function restore() {
    if (!id) return;

    setRestoring(true);

    try {
      await config.api.activate(id);
      load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError(`Could not restore this ${config.singular}.`, 0));
    } finally {
      setRestoring(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Evidence Management',
    { label: 'Settings', to: '/evidence/settings' },
    { label: config.title, to: config.route },
    item?.name ?? config.singular,
  ];

  if (error && !item) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const Icon = { kinds: Shapes, types: Tags, 'disposal-types': PackageX }[vocabulary];

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id={prefix}
        header={
          <ProfileHeader
            id={`${prefix}-header`}
            icon={<Icon className="h-6 w-6" aria-hidden="true" />}
            title={item.name}
            meta={config.hasFileFlag ? [KEPT_AS(item)] : []}
            status={<StatusText tone={item.isActive ? 'active' : 'inactive'}>{item.isActive ? 'Active' : 'Deleted'}</StatusText>}
            createdBy={item.createdByName}
            createdAt={item.createdAt}
          />
        }
        banner={
          item.isActive ? (
            <ProfileBanner
              id={`${prefix}-banner`}
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message={`This ${config.singular} is offered when evidence is received.`}
            />
          ) : (
            <ProfileBanner
              id={`${prefix}-banner`}
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={`This ${config.singular} is deleted. It is no longer offered; items already carrying it keep it.`}
              action={
                <PermissionGate permission={`${config.module}.delete`}>
                  <Button
                    id={`${prefix}-restore`}
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => setConfirmingRestore(true)}
                    disabled={restoring}
                    loading={restoring}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id={`${prefix}-details`}
          title="Details"
          menuItems={[{ key: 'edit', label: 'Edit', permission: `${config.module}.update`, onClick: () => setEditing(true) }]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Name', value: item.name },
              ...(config.hasFileFlag ? [{ label: 'Kept as', value: KEPT_AS(item) }] : []),
              { label: 'Description', value: item.description },
            ]}
          />
        </ProfileCard>

        <ProfileCard id={`${prefix}-audit`} title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: item.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(item.createdAt)} className="font-mono">
                    {formatDateTime(item.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: item.updatedByName },
              {
                label: 'Updated',
                value: item.updatedAt ? (
                  <time dateTime={toIso(item.updatedAt)} className="font-mono">
                    {formatDateTime(item.updatedAt)}
                  </time>
                ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {/* A banner action that WRITES confirms. */}
      {confirmingRestore ? (
        <ConfirmDialog
          open
          onClose={() => setConfirmingRestore(false)}
          onConfirm={() => {
            setConfirmingRestore(false);
            void restore();
          }}
          title={`Restore ${config.singular}`}
          requireConfirm
          loading={restoring}
          id={`${prefix}-restore-dialog`}
          body={
            <p>
              &quot;{item.name}&quot; is offered again when evidence is received. A restore is refused when a
              live {config.singular} has taken its name meanwhile.
            </p>
          }
        />
      ) : null}

      {editing ? (
        <EvidenceVocabularyModal
          vocabulary={vocabulary}
          editing={item}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
